// What form a Japanese word is in, when it is not in its dictionary form.
//
// THE GAP THIS FILLS. Tap 食べました in a sentence and the panel says "to eat". That is the right
// meaning — the gloss is looked up under the base form, because 食べました is not in JMdict and
// 食べる is (build-japanese-pair.mjs) — but nothing says that this IS 食べる, and nothing says what
// the ました is doing. 12,874 of this course's 56,599 tappable tokens are inflected: 22.7% of
// every word a learner can tap is a word whose shape has never been explained to them.
//
// GENERATED, NOT READ OFF THE ENDING. The obvious implementation is a table of suffixes — ました
// means polite past — and it is wrong in both directions. It calls 何歳だ the past of 何, because
// だ ends it, when だ there is the copula and 何 is not a verb at all. And it misses every する
// form, because し does not look like anything that could have come from する.
//
// So this conjugates the BASE and looks the surface up in what comes out. A name is claimed only
// when the base really does inflect to exactly this surface, which makes both mistakes impossible:
// 何 has no conjugation that produces 何歳だ, and する's own table has し in it.
//
// WHERE THE BASE COMES FROM. The corpus, which carries `b` on a sentence word — the segmenter's
// lemma, decided when the sentence was built rather than guessed in the app. It is not always an
// inflection: the segmenter gives 私たち the lemma 私 and 何時 the lemma 何, and neither is 私 or 何
// in some form. Those produce no match, and the panel then says nothing, which is the point.
//
// A VERB'S CLASS IS NOT WRITTEN DOWN, so both possibilities are generated: 見る could be ichidan
// (見て) or godan (見って), and only one of those will ever be the surface in front of us. Where a
// form is ambiguous the first name wins, and the ambiguity is between two names for one form
// rather than between six readings of a word.

/** The five vowel rows a godan verb steps through, keyed by its dictionary-form ending. */
const I: Record<string, string> = { う: 'い', く: 'き', ぐ: 'ぎ', す: 'し', つ: 'ち', ぬ: 'に', ぶ: 'び', む: 'み', る: 'り' };
const A: Record<string, string> = { う: 'わ', く: 'か', ぐ: 'が', す: 'さ', つ: 'た', ぬ: 'な', ぶ: 'ば', む: 'ま', る: 'ら' };
const E: Record<string, string> = { う: 'え', く: 'け', ぐ: 'げ', す: 'せ', つ: 'て', ぬ: 'ね', ぶ: 'べ', む: 'め', る: 'れ' };
const O: Record<string, string> = { う: 'お', く: 'こ', ぐ: 'ご', す: 'そ', つ: 'と', ぬ: 'の', ぶ: 'ぼ', む: 'も', る: 'ろ' };

/**
 * The て form of a godan verb, which is where the sound changes rather than the ending.
 *
 * 待つ is 待って and 読む is 読んで: the kana before て is decided by the verb's own last kana, and
 * the た form follows it exactly. This is the part a learner meets as a table and never derives.
 */
const TE: Record<string, string> = { う: 'って', つ: 'って', る: 'って', む: 'んで', ぶ: 'んで', ぬ: 'んで', く: 'いて', ぐ: 'いで', す: 'して' };

/** 行く is the one godan verb whose て form does not follow its ending: 行って, never 行いて. */
const IRREGULAR_TE: Record<string, string> = { 行く: '行って', いく: 'いって' };

/** The names, in the source language. Every one of them is a phrase the app translates. */
export const FORM_NAMES = [
  'polite', 'polite past', 'polite negative', 'polite past negative', 'polite suggestion',
  'past', 'negative', 'past negative', 'the て form', 'the negative て form',
  'ongoing', 'polite ongoing', 'ongoing, casually',
  'wanting to', 'wanting to, in the past', 'not wanting to',
  'potential', 'passive', 'potential or passive', 'causative',
  'conditional', 'suggestion', 'command',
  'as an adverb', 'before a noun', 'as a noun', 'plain',
] as const;

export type FormName = (typeof FORM_NAMES)[number];

/**
 * Every form `base` can take, as form -> what to call it.
 *
 * Exported because the table IS the claim: a test can conjugate 食べる and check that 食べません is
 * in it under the name a textbook would use, which is a far more direct thing to assert than
 * whether some sentence in the corpus happened to be named.
 */
export function formsOf(base: string): Map<string, FormName> {
  const out = new Map<string, FormName>();
  const put = (form: string, name: FormName): void => {
    if (form && !out.has(form)) out.set(form, name);
  };
  const last = base.slice(-1);
  const stem = base.slice(0, -1);

  /** The forms that hang off a verb's ます-stem, plus its て and た, whatever class it is. */
  const verb = (masu: string, te: string, ta: string): void => {
    put(`${masu}ます`, 'polite');
    put(`${masu}ました`, 'polite past');
    put(`${masu}ません`, 'polite negative');
    put(`${masu}ませんでした`, 'polite past negative');
    put(`${masu}ましょう`, 'polite suggestion');
    put(`${masu}たい`, 'wanting to');
    put(`${masu}たかった`, 'wanting to, in the past');
    put(`${masu}たくない`, 'not wanting to');
    put(te, 'the て form');
    put(ta, 'past');
    put(`${te}いる`, 'ongoing');
    put(`${te}います`, 'polite ongoing');
    put(`${te}いた`, 'ongoing');
    // 知ってる for 知っている. Spoken Japanese drops the い and the corpus is spoken Japanese.
    put(`${te}る`, 'ongoing, casually');
    put(`${te}た`, 'ongoing, casually');
  };

  if (base.endsWith('する')) {
    const s = base.slice(0, -2);
    verb(`${s}し`, `${s}して`, `${s}した`);
    put(`${s}しない`, 'negative');
    put(`${s}しなかった`, 'past negative');
    put(`${s}しなくて`, 'the negative て form');
    put(`${s}できる`, 'potential');
    put(`${s}される`, 'passive');
    put(`${s}させる`, 'causative');
    put(`${s}すれば`, 'conditional');
    put(`${s}したら`, 'conditional');
    put(`${s}しよう`, 'suggestion');
    put(`${s}しろ`, 'command');
    put(`${s}するの`, 'as a noun');
    put(`${s}すること`, 'as a noun');
  }
  if (base === '来る' || base === 'くる') {
    const k = base === '来る' ? '来' : 'き';
    verb(k, `${k}て`, `${k}た`);
    put(`${k}ない`, 'negative');
    put(`${k}なかった`, 'past negative');
    put(base === '来る' ? '来られる' : 'こられる', 'potential or passive');
    put(base === '来る' ? '来れば' : 'くれば', 'conditional');
  }
  if (last === 'る') {
    // As an ichidan verb, which 見る is and 走る is not. Both tables are generated and the surface
    // decides; a form only one of them produces can only have come from that one.
    verb(stem, `${stem}て`, `${stem}た`);
    put(`${stem}ない`, 'negative');
    put(`${stem}なかった`, 'past negative');
    put(`${stem}なくて`, 'the negative て form');
    put(`${stem}られる`, 'potential or passive');
    put(`${stem}させる`, 'causative');
    put(`${stem}れば`, 'conditional');
    put(`${stem}たら`, 'conditional');
    put(`${stem}よう`, 'suggestion');
    put(`${stem}ろ`, 'command');
  }
  if (I[last]) {
    // As a godan verb. 行く's て form is the one exception in the language and is named outright.
    const te = IRREGULAR_TE[base] ?? stem + (TE[last] ?? '');
    verb(stem + I[last], te, te.replace(/て$/u, 'た').replace(/で$/u, 'だ'));
    put(`${stem}${A[last]}ない`, 'negative');
    put(`${stem}${A[last]}なかった`, 'past negative');
    put(`${stem}${A[last]}なくて`, 'the negative て form');
    put(`${stem}${E[last]}る`, 'potential');
    put(`${stem}${A[last]}れる`, 'passive');
    put(`${stem}${A[last]}せる`, 'causative');
    put(`${stem}${E[last]}ば`, 'conditional');
    put(`${stem}${O[last]}う`, 'suggestion');
    put(`${stem}${E[last]}`, 'command');
  }
  if (last === 'い' && base.length > 1) {
    put(`${stem}く`, 'as an adverb');
    put(`${stem}くない`, 'negative');
    put(`${stem}かった`, 'past');
    put(`${stem}くなかった`, 'past negative');
    put(`${stem}くて`, 'the て form');
    put(`${stem}ければ`, 'conditional');
    put(`${stem}さ`, 'as a noun');
  }
  // THE COPULA, WHICH ANY WORD CAN TAKE, and which is why a suffix table calls 何歳だ a past
  // tense. It goes last so a verb's own forms win where the two could collide.
  put(`${base}です`, 'polite');
  put(`${base}でした`, 'polite past');
  put(`${base}じゃない`, 'negative');
  put(`${base}ではない`, 'negative');
  put(`${base}じゃなかった`, 'past negative');
  put(`${base}だった`, 'past');
  put(`${base}だ`, 'plain');
  put(`${base}な`, 'before a noun');
  put(`${base}に`, 'as an adverb');
  put(`${base}の`, 'as a noun');
  return out;
}

/**
 * What to call `surface`, given the dictionary form it came from — or null if nothing fits.
 *
 * Null is the common answer and the honest one: the lemma on a sentence word is not always an
 * inflection (私たち is filed under 私), and a form nobody can name is better left unnamed than
 * given a guess with a textbook word attached to it.
 */
export function formOf(surface: string, base: string | undefined): FormName | null {
  if (!base || !surface || surface === base) return null;
  return formsOf(base).get(surface) ?? null;
}

/**
 * The shapes to TEACH, for a word whose class the dictionary states.
 *
 * THE OTHER DIRECTION FROM formOf. That one is handed a surface and says what it is, and it can
 * afford to generate every class's forms because the surface picks one: only 見る's ichidan table
 * produces 見て. Producing a table has no surface to pick with, so it needs the class — 見て or
 * 走って is not something a card can work out, and `c` on the word is JMdict saying which.
 *
 * FOUR SHAPES, not the twenty this file can generate. Polite, past, negative and the て form are
 * what a beginner meets first and what the sentence decks are written in; a full paradigm on a
 * flashcard is a reference table, and a learner who wants one has a textbook.
 */
export interface Conjugation {
  name: FormName;
  form: string;
}

/** How many shapes a card has room to teach. */
const TEACH = 4;

/**
 * A word's first four forms, or an empty list when its class is unknown or it does not inflect.
 *
 * IN THE TABLE'S OWN ORDER rather than a fixed list of names, because the four are not the same
 * four for every class: a verb teaches polite, past, negative and the て form, and a na-adjective
 * has no て form of its own — what it needs in that slot is 好きな, the shape before a noun.
 */
export function conjugate(base: string, cls: string | undefined): Conjugation[] {
  if (!base || !cls) return [];
  const table = classTable(base, cls);
  if (!table) return [];
  return [...table].slice(0, TEACH).map(([name, form]) => ({ name, form }));
}

/**
 * name -> form for ONE class, which is the inverse of what formsOf builds.
 *
 * formsOf maps form -> name because recognition asks "what is this shape"; a table asks "what is
 * the past", so this walks the same generated forms the other way. Where a name has two forms in
 * one class the first wins, which is the same rule and the same order.
 */
function classTable(base: string, cls: string): Map<FormName, string> | null {
  const stem = base.slice(0, -1);
  const last = base.slice(-1);
  const rows = new Map<FormName, string>();
  const set = (name: FormName, form: string): void => {
    if (form && !rows.has(name)) rows.set(name, form);
  };
  const verb = (masu: string, te: string, ta: string, nai: string): void => {
    set('polite', `${masu}ます`);
    set('past', ta);
    set('negative', nai);
    set('the て form', te);
  };
  if (cls === 'vs-i' || cls === 'vs-s') {
    const s = base.endsWith('する') ? base.slice(0, -2) : base;
    verb(`${s}し`, `${s}して`, `${s}した`, `${s}しない`);
  } else if (cls === 'vk') {
    // 来る: 来ます, 来た, 来ない, 来て. Written in kanji or kana as the word itself is.
    const k = base === 'くる' ? 'き' : base.slice(0, -1);
    verb(k, `${k}て`, `${k}た`, base === 'くる' ? 'こない' : `${base.slice(0, -1)}ない`);
  } else if (cls === 'v1' || cls === 'v1-s') {
    verb(stem, `${stem}て`, `${stem}た`, `${stem}ない`);
  } else if (cls.startsWith('v5')) {
    // The one exception in the language, which JMdict marks as its own class: 行って, not 行いて.
    const te = cls === 'v5k-s' || IRREGULAR_TE[base] ? (IRREGULAR_TE[base] ?? `${stem}って`) : stem + (TE[last] ?? '');
    if (!I[last]) return null;
    verb(
      stem + I[last],
      te,
      te.replace(/て$/u, 'た').replace(/で$/u, 'だ'),
      // ある is v5r-i because its negative is ない — no 「あらない」 exists. The class says so, so
      // the table does not have to pretend otherwise.
      cls === 'v5r-i' ? 'ない' : `${stem}${A[last]}ない`,
    );
  } else if (cls === 'adj-i' || cls === 'adj-ix') {
    // いい is adj-ix because it inflects as よい: よかった, not 「いかった」.
    const root = cls === 'adj-ix' ? `${base.slice(0, -2)}よ` : stem;
    set('polite', `${base}です`);
    set('past', `${root}かった`);
    set('negative', `${root}くない`);
    set('the て form', `${root}くて`);
  } else if (cls === 'adj-na') {
    set('polite', `${base}です`);
    set('past', `${base}でした`);
    set('negative', `${base}じゃない`);
    // A na-adjective has no て form of its own -- it borrows the copula's -- and the shape a
    // learner actually needs from it is the one before a noun: 好きな本.
    set('before a noun', `${base}な`);
  } else {
    return null;
  }
  return rows.size ? rows : null;
}
